import { FC, useCallback, useRef } from 'react';
import * as tracking from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { peopleAlsoBrowsed } from '../pdpMessages';
import {
    PdpRespProductTileCarousel,
    PdpRespProductTileCarouselProps,
} from './PdpRespProductTileCarousel/PdpRespProductTileCarousel';

// Helpers
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    trackSimilarItemClick,
    trackCarouselItemImpressions,
    PERSONALIZATION_MODULE_ITEMS_LIST,
    trackPersonalizedItemsRendered,
} from '../tracking/similarItemsTracking';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

// types
import { PdpRespPersonalizedItemsCarousel_viewer$key } from './__generated__/PdpRespPersonalizedItemsCarousel_viewer.graphql';
import { PdpRespPersonalizedItemsCarousel_user$key } from './__generated__/PdpRespPersonalizedItemsCarousel_user.graphql';

type Props = {
    viewer: PdpRespPersonalizedItemsCarousel_viewer$key;
    user: PdpRespPersonalizedItemsCarousel_user$key | null;
    currency: string;
};

const userFragment = graphql`
    fragment PdpRespPersonalizedItemsCarousel_user on User {
        ...PdpRespProductTileCarousel_user
    }
`;

const viewerFragment = graphql`
    fragment PdpRespPersonalizedItemsCarousel_viewer on Viewer {
        ...PdpRespProductTileCarousel_viewer

        personalizedItems(itemId: $itemId) @include(if: $personalizationModuleEnabled) {
            associatedItems {
                associationType
                item {
                    ...PdpRespProductTileCarousel_items
                        @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                }
            }
        }
    }
`;

const MIN_PERSONALIZED_ITEMS_COUNT = 5;

export const PdpRespPersonalizedItemsCarousel: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const impressionTrackingRef = useRef({});
    const associatedItems = viewer.personalizedItems?.associatedItems || [];
    const personalizedItems = associatedItems
        .map(association => association?.item || null)
        .filter(filterNulls);

    const onItemsVisible = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onItemsVisible']>
    >(
        (visibleItems, startIndex) =>
            trackCarouselItemImpressions({
                visibleItems,
                startIndex,
                impressionTrackingRef,
                list: PERSONALIZATION_MODULE_ITEMS_LIST,
            }),
        []
    );

    const onClickItemTile = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onClickItemTile']>
    >((tileItem, index) => {
        trackSimilarItemClick(index, tileItem, PERSONALIZATION_MODULE_ITEMS_LIST);
    }, []);

    if (personalizedItems.length < MIN_PERSONALIZED_ITEMS_COUNT) {
        return null;
    }

    return (
        <PdpRespProductTileCarousel
            viewer={viewer}
            items={personalizedItems}
            user={user}
            currency={currency}
            title={peopleAlsoBrowsed}
            location={locations.PERSONALIZED_ITEM_LIST}
            onCarouselRendered={trackPersonalizedItemsRendered}
            onClickItemTile={onClickItemTile}
            onItemsVisible={onItemsVisible}
            onClickArrow={() => {
                tracking.trackEvent(
                    {
                        category: 'product',
                        isInteractiveEvent: true,
                        action: 'pdp module arrow click',
                        label: PERSONALIZATION_MODULE_ITEMS_LIST,
                    },
                    null
                );
            }}
        />
    );
};
